import { NextRouter } from 'next/router';

/**
 * Known route segments that indicate a user/org-scoped page
 */
const USER_ORG_ROUTE_SEGMENTS = [
  'apps',
  'models',
  'workflows',
  'datasets',
  'modules',
  'compute',
  'control-center',
  'settings',
  'playground',
  'home',
] as const;

/**
 * Extracts the userOrOrgId from the URL path, handling both normal navigation
 * and 404 cases where router.query might not be populated.
 * Only returns a user ID if the URL is actually a user/org-scoped page we care about.
 */
export function extractUserOrOrgId(router: NextRouter): string | undefined {
  const { query, asPath, pathname } = router;

  // Early return for invalid router state
  if (!asPath || !pathname) {
    return undefined;
  }

  // If we're on an explore page, return undefined
  if (asPath.includes('/explore')) {
    return undefined;
  }

  // Try to get from router query first (normal case)
  const userIdFromQuery = query.userOrOrgId as string;
  if (userIdFromQuery && pathname.includes('[userOrOrgId]')) {
    return userIdFromQuery;
  }

  // Fallback to extracting from asPath (404 case)
  // Only extract if the URL matches our expected user/org-scoped patterns
  const routeSegmentPattern = USER_ORG_ROUTE_SEGMENTS.join('|');
  const userOrgPattern = new RegExp(`^/([^/]+)/(${routeSegmentPattern})`);
  const match = asPath.match(userOrgPattern);

  if (match && match[1]) {
    // Validate that the extracted ID is not empty and doesn't contain invalid characters
    const userId = match[1].trim();
    if (userId && !userId.includes('..') && !userId.includes(' ')) {
      return userId;
    }
  }

  return undefined;
}

/**
 * Checks if the current route is an app resource route (contains [appId])
 */
export function isAppResourceRoute(router: NextRouter): boolean {
  const { pathname, asPath } = router;

  // Early return for invalid router state
  if (!pathname || !asPath) {
    return false;
  }

  return pathname.includes('/[userOrOrgId]/[appId]') || asPath.includes('/[appId]');
}

/**
 * Checks if the current route is an explore route (contains /explore)
 */
export function isExploreRoute(router: NextRouter): boolean {
  const { pathname, asPath } = router;

  // Early return for invalid router state
  if (!pathname || !asPath) {
    return false;
  }

  return pathname.includes('/explore') || asPath.includes('/explore');
}

/**
 * Replaces the userOrOrgId in the current URL path
 * Handles both normal URLs and direct path construction for 404 cases
 */
export function replaceUserOrOrgIdInUrl(router: NextRouter, newUserOrOrgId: string): string {
  const { asPath } = router;

  // Early return for invalid inputs
  if (!asPath || !newUserOrOrgId.trim()) {
    return asPath || '/';
  }

  // Replace the first path segment (userOrOrgId) with the new one
  return asPath.replace(/^\/[^/]+\//, `/${newUserOrOrgId.trim()}/`);
}
